// -----------------------------------------------------------------------------
// FollowUpPanel  (Task #26 — revised #26H — sidebar redirect)
//
// Shown when item_type === 'LEAD_NEXT_ACTION' or 'OPEN_FOLLOW_UP'.
//
// Step 1 — "How did you follow up?" channel picker:
//   Call · WhatsApp · Email · Note
//
// Step 2:
//   Call  → LogCallPanel (inline form)
//   Note  → AddNotePanel (inline form)
//   Email → calls onOpenSidebarTab('email')  + shows SidebarRedirectConfirmation
//   WA    → calls onOpenSidebarTab('whatsapp') + shows SidebarRedirectConfirmation
//
// Email/WA composers live in the OpenConsultationPopup right sidebar. The
// confirmation view lets the user mark the follow-up done once they have sent
// the message in the sidebar.
//
// Graceful degradation: if onOpenSidebarTab is absent the confirmation view is
// still shown so onComplete can still be triggered.
//
// Completing any channel calls onComplete({...item, resolvedAction:'follow_up'})
// so the orchestrator builds the required resolved_action payload for the BE.
//
// Props mirror the standard panel contract:
//   item, practiceId, leadId, leadDetail, onClose, onComplete, handleReloadLeads,
//   onOpenSidebarTab
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import Iconify from 'src/components/Iconify';
import LogCallPanel from './LogCallPanel';
import AddNotePanel from './AddNotePanel';
import SidebarRedirectConfirmation from './SidebarRedirectConfirmation';

// ---------------------------------------------------------------------------
// Channel definitions
// ---------------------------------------------------------------------------
const CHANNELS = [
    {
        key: 'call',
        label: 'Call',
        icon: 'solar:phone-bold',
        color: '#00A76F',
        bg: 'rgba(0,167,111,0.08)',
        border: 'rgba(0,167,111,0.20)',
    },
    {
        key: 'whatsapp',
        label: 'WhatsApp',
        icon: 'ic:baseline-whatsapp',
        color: '#25D366',
        bg: 'rgba(37,211,102,0.08)',
        border: 'rgba(37,211,102,0.20)',
    },
    {
        key: 'email',
        label: 'Email',
        icon: 'mdi:email-outline',
        color: '#078DEE',
        bg: 'rgba(7,141,238,0.08)',
        border: 'rgba(7,141,238,0.20)',
    },
    {
        key: 'note',
        label: 'Note',
        icon: 'mdi:note-text-outline',
        color: '#FFAB00',
        bg: 'rgba(255,171,0,0.08)',
        border: 'rgba(255,171,0,0.20)',
    },
];

// ---------------------------------------------------------------------------
// Shared containment sx — applied to every sub-panel wrapper so no child
// component can overflow the ActionPanel card's allocated slot.
// ---------------------------------------------------------------------------
const SUB_PANEL_SX = {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
};

// ---------------------------------------------------------------------------
// ChannelPicker — Step 1
// ---------------------------------------------------------------------------
function ChannelPicker({ onSelect }) {
    return (
        <Box
            data-testid="follow-up-channel-picker"
            sx={{
                ...SUB_PANEL_SX,
                px: 2.5,
                py: 2,
                gap: 1.5,
                // Full-width channel buttons (user request) — fill the panel column.
                width: '100%',
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#1d1d1f',
                        letterSpacing: '-0.02em',
                    }}
                >
                    How did you follow up?
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, letterSpacing: '-0.005em' }}>
                    Choose the channel you used to contact this patient.
                </Typography>
            </Box>

            <Stack spacing={1}>
                {CHANNELS.map((ch) => (
                    <Box
                        key={ch.key}
                        component="button"
                        data-testid={`follow-up-channel-${ch.key}`}
                        onClick={() => onSelect(ch.key)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            borderRadius: '12px',
                            border: `1.5px solid ${ch.border}`,
                            bgcolor: ch.bg,
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                filter: 'brightness(0.96)',
                                transform: 'translateY(-1px)',
                                boxShadow: `0 3px 10px ${ch.bg}`,
                            },
                            '&:active': { transform: 'none' },
                        }}
                    >
                        <Box
                            sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '10px',
                                bgcolor: ch.bg,
                                border: `1px solid ${ch.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Iconify icon={ch.icon} width={20} height={20} sx={{ color: ch.color }} />
                        </Box>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#1d1d1f',
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {ch.label}
                        </Typography>
                        <Iconify
                            icon="eva:chevron-right-fill"
                            width={16}
                            height={16}
                            sx={{ color: '#86868b', ml: 'auto' }}
                        />
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

ChannelPicker.propTypes = {
    onSelect: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// FollowUpPanel — orchestrates picker → sub-panel
// ---------------------------------------------------------------------------
export default function FollowUpPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onClose,
    onComplete,
    handleReloadLeads,
    onOpenSidebarTab,
    noSidebar,
}) {
    const [channel, setChannel] = useState(null);

    const handleChannelComplete = useCallback(() => {
        if (!onComplete) return;
        // LEAD_NEXT_ACTION / OPEN_FOLLOW_UP require resolved_action in the
        // completion payload — the orchestrator reads completedItem.resolvedAction
        // and builds { resolved_action: 'follow_up' } for the BE (else 400).
        onComplete({ ...item, resolvedAction: 'follow_up' });
    }, [onComplete, item]);

    // For Email/WA: open the matching sidebar tab (if callback available),
    // then switch to the confirmation view inside the action panel.
    const handleChannelSelect = useCallback((ch) => {
        if ((ch === 'email' || ch === 'whatsapp') && onOpenSidebarTab) {
            onOpenSidebarTab(ch);
        }
        setChannel(ch);
    }, [onOpenSidebarTab]);

    const handleBack = () => setChannel(null);

    // Back button rendered above Call/Note sub-panels
    const BackBtn = (
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, flexShrink: 0 }}>
            <Button
                data-testid="follow-up-back-btn"
                size="small"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={14} height={14} />}
                onClick={handleBack}
                sx={{
                    color: '#078DEE',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    px: 0.5,
                    '&:hover': { bgcolor: 'rgba(7,141,238,0.06)' },
                }}
            >
                Back
            </Button>
        </Box>
    );

    // Step 1: channel picker
    if (!channel) {
        return <ChannelPicker onSelect={handleChannelSelect} />;
    }

    const commonProps = {
        item,
        practiceId,
        leadId,
        leadDetail,
        onClose,
        onComplete: handleChannelComplete,
        handleReloadLeads,
    };

    // ── Email / WhatsApp — open sidebar composer + show confirmation ──────────
    if (channel === 'email' || channel === 'whatsapp') {
        return (
            <SidebarRedirectConfirmation
                channel={channel}
                body="Composer opened in the sidebar — send your message there, then mark this follow-up complete."
                doneLabel="Mark follow-up done"
                onBack={handleBack}
                onComplete={handleChannelComplete}
                practiceId={practiceId}
                leadId={leadId}
                leadDetail={leadDetail}
                noSidebar={noSidebar}
            />
        );
    }

    // ── Call ──────────────────────────────────────────────────────────────────
    if (channel === 'call') {
        return (
            <Box sx={SUB_PANEL_SX}>
                {BackBtn}
                <LogCallPanel {...commonProps} />
            </Box>
        );
    }

    // ── Note ──────────────────────────────────────────────────────────────────
    if (channel === 'note') {
        return (
            <Box sx={SUB_PANEL_SX}>
                {BackBtn}
                <AddNotePanel {...commonProps} />
            </Box>
        );
    }

    return null;
}

FollowUpPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onComplete: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to open the matching right-sidebar tab. */
    onOpenSidebarTab: PropTypes.func,
    /** Additive, default false. When true, Email/WA confirmation copy is sidebar-free. */
    noSidebar: PropTypes.bool,
};

FollowUpPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
    handleReloadLeads: null,
    onOpenSidebarTab: null,
    noSidebar: false,
};
